import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import AlertText from 'components/AlertText';
import { i18n } from 'i18n';
import {
  MINE_MODEL_KEY,
  MineModelDto,
  ProjectModule,
  StopeLocationDto
} from 'providers/api';
import React, { useContext, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useProject } from 'modules/project/providers/project';
import useScenario from 'modules/project/providers/scenario/use';
import PoursTabPanel from './PoursTab/PoursTabPanel';
import RecipesTabPanel from './RecipesTab/RecipesTabPanel';
import StopeTitleCard from './StopeTitleCard';
import { Model } from '../hooks/useModelState';
import PCStope from '../MineModel/Stopes/PCStope';
import { StopePanelTab } from '../MineModel/types';
import { navigateToScene } from '../Scenes/scene-utils';
import SceneManagerContext from '../hooks/SceneManagerProvider/sceneManagerContext';

interface StopePanelPageProps {
  model: Model;
  onStopeEdit: (payload: { nodeId: string; stopeId: string }) => void;
  onAddStope?: (payload: { nodeId: string }) => void;
}

const StopePanelPage = ({ model, onStopeEdit, onAddStope }: StopePanelPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ stopeId: StopeLocationDto['stopeId'], tab: string }>();
  const { state: { enabledModules } } = useProject();
  const stope = model.selectedStope;
  const stopeOverview = model.mineOverview.find((summary) => summary.stopeIdentifier.stopeId === stope?.data.stopeId);

  const queryClient = useQueryClient();
  const { activeScenarioId } = useScenario();

  const handleGeometryUploaded = React.useCallback(async () => {
    const mineModelQueryKey = [MINE_MODEL_KEY, { mineModelId: model.project.entityId, scenarioId: activeScenarioId ?? undefined }];
    await queryClient.refetchQueries(mineModelQueryKey);
    const freshModel = queryClient.getQueryData<MineModelDto>(mineModelQueryKey);
    if (freshModel && stope) {
      const updatedStopeData = freshModel.reticulationData?.stopes?.find(
        (s) => s.stopeId === stope.data.stopeId,
      );
      if (updatedStopeData) {
        model.setSelectedStope(new PCStope(stope.originNode, updatedStopeData));
      }
    }
  }, [model, stope, queryClient, activeScenarioId]);

  const { stopeIdForEdit, nodeIdForEdit } = useMemo(() => {
    const sId = stope?.data.stopeId ?? params.stopeId ?? '';
    let nId = stope?.originNode?.id ?? '';

    if (!nId && sId) {
      const matchingNodeId = Object.entries(model.stopes ?? {}).find(
        ([, stopes]) => stopes?.some((s) => s?.data?.stopeId === sId),
      )?.[0];
      nId = matchingNodeId ?? '';
    }

    return { stopeIdForEdit: sId ?? '', nodeIdForEdit: nId ?? '' };
  }, [model.stopes, params.stopeId, stope?.data?.stopeId, stope?.originNode?.id]);

  const context = useContext(SceneManagerContext);
  const hasGeometry = !!stope?.data.stopeGeometryFile;
  const stopeFillEnabled = enabledModules.some((m) => m === ProjectModule.StopeFill);

  const handleTabChange = (_e: React.SyntheticEvent, newValue: StopePanelTab) => {
    if (newValue === 'pours' && hasGeometry && stopeFillEnabled && stope && context) {
      navigateToScene('STOPE_FILL', { model, context, stopeId: stope.data.stopeId, stope });
      return;
    }
    model.setRecipeState({ ...model.recipeState, recipeTab: newValue });
  };

  React.useEffect(() => {
    const requestedTab = (location.state as { recipeTab?: StopePanelTab } | undefined)?.recipeTab;
    if ((requestedTab === 'recipes' || requestedTab === 'pours')
      && model.recipeState?.recipeTab !== requestedTab) {
      model.setRecipeState({ ...model.recipeState, recipeTab: requestedTab });
    }
  }, [location.state, model]);

  return (
    <>
      <Box>
        <StopeTitleCard
          stopeId={stope?.data.stopeId ?? ''}
          stopeName={stope?.data.stopeName ?? ''}
          hasIncompletePours={!!stopeOverview?.nextPourDate}
          hasGeometry={!!stope?.data.stopeGeometryFile}
          stopeComplete={!!stopeOverview?.isComplete}
          completedVolume={stopeOverview?.completedVolume ?? 0}
          plannedVolume={stopeOverview?.plannedVolume ?? 0}
          volume={stope?.data.volume}
          unitSystemPreference={model.project.unitSystemPreference}
          customUnitFields={model.project.customUnitFields ?? []}
          nodeId={nodeIdForEdit}
          model={model}
          onStopeEdit={() => {
            if (nodeIdForEdit && stopeIdForEdit) {
              onStopeEdit({ nodeId: nodeIdForEdit, stopeId: stopeIdForEdit });
            } else {
              // eslint-disable-next-line no-console
              console.warn('Cannot open Stope Edit: missing ids', { nodeIdForEdit, stopeIdForEdit, stope });
            }
          }}
          onAddStope={onAddStope ? () => {
            if (nodeIdForEdit) {
              onAddStope({ nodeId: nodeIdForEdit });
            }
          } : undefined}
          onGeometryUploaded={handleGeometryUploaded}
          onDeleteSuccess={() => {
            model.unsetSelectedStope();
            if (context) {
              navigateToScene('DEFAULT', {
                model,
                context,
                replace: true,
              });
            }
            navigate(0);
          }}
        />
      </Box>

      {!stope?.data && (
        <AlertText severity="info">{i18n._('No data has been uploaded for this stope')}</AlertText>
      )}

      <Box sx={{ py: 2 }}>
        <Paper square>
          <Tabs
            variant="fullWidth"
            value={
              (model.recipeState?.recipeTab as StopePanelTab) ?? (params.tab ? 'pours' : 'recipes')
            }
            indicatorColor="secondary"
            textColor="secondary"
            onChange={handleTabChange}
            aria-label="stope menu"
          >
            <Tab value="recipes" label={i18n._('Recipes')} />
            <Tab value="pours" label={i18n._('Pours')} />
          </Tabs>
        </Paper>
      </Box>

      {model.recipeState?.recipeTab === 'pours' ? (
        <PoursTabPanel
          backFillPlantNodeId={model.activeBackfillPlant?.id}
          projectId={model.project.entityId}
          stopeId={stope?.data.stopeId || ''}
          displayUnitPreferences={model.project.displayUnitPreferences ?? []}
          customUnitFields={model.project.customUnitFields ?? []}
          unitSystemPreference={model.project.unitSystemPreference}
          fillType={model.project.fillType}
          throughputControlType={model.project.throughput}
          isHideAbandonedPour={model.project.isHideAbandonedPour}
          model={model}
          pumpType={model.project.pumpType}
        />
      ) : (
        <RecipesTabPanel
          model={model}
          stopeComplete={stopeOverview?.isComplete ?? false}
          pourPresetGroupId={stope?.data.pourPresetGroupId}
        />
      )}
    </>
  );
};

export default StopePanelPage;
